import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, Instagram, Music2, Youtube, Mail, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { fmtMoney } from '../lib/format';
import { dealTalentIds } from '../lib/deals';
import InitialsAvatar from '../components/InitialsAvatar';

const emptyTalent = () => ({ name: '', email: '', handle: '', bio: '', instagram: '', tiktok: '', youtube: '', rate_card: '', picture: '', commission_percent: '' });

const AddTalentDialog = ({ open, onOpenChange }) => {
  const { addTalent } = useDeals();
  const [form, setForm] = useState(emptyTalent());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setError(null);
    if (!form.name || !form.email) { setError('Name and email are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      payload.commission_percent = payload.commission_percent === '' ? null : Number(payload.commission_percent);
      await addTalent(payload);
      setForm(emptyTalent());
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add talent');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>add talent</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">They’ll get access when they sign in with this Gmail address.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ava Mercer" className="mt-1" /></div>
          <div><Label>Gmail address</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="ava@gmail.com" className="mt-1" /></div>
          <div><Label>Handle</Label><Input value={form.handle} onChange={(e) => set('handle', e.target.value)} placeholder="@avamercer" className="mt-1" /></div>
          <div><Label>Instagram</Label><Input value={form.instagram} onChange={(e) => set('instagram', e.target.value)} placeholder="instagram.com/…" className="mt-1" /></div>
          <div><Label>TikTok</Label><Input value={form.tiktok} onChange={(e) => set('tiktok', e.target.value)} placeholder="tiktok.com/@…" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>YouTube</Label><Input value={form.youtube} onChange={(e) => set('youtube', e.target.value)} placeholder="youtube.com/@…" className="mt-1" /></div>
          <div className="md:col-span-2"><Label>Default commission % (agency's cut on this talent's deals)</Label><Input type="number" min="0" max="100" value={form.commission_percent} onChange={(e) => set('commission_percent', e.target.value)} placeholder="e.g. 20" className="mt-1 w-32" /></div>
          <div className="md:col-span-2"><Label>Bio</Label><Textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} rows={3} className="mt-1" placeholder="Short intro…" /></div>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>cancel</Button>
          <Button disabled={saving} onClick={submit} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">{saving ? 'adding…' : 'add talent'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Talent = () => {
  const { user } = useAuth();
  const { talent, deals, loading, removeTalent } = useDeals();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!loading && user?.role === 'talent') {
    return <Navigate to={`/talent/${user.user_id}`} replace />;
  }

  const filtered = talent.filter((t) => !q.trim() || `${t.name} ${t.handle || ''} ${t.email}`.toLowerCase().includes(q.toLowerCase()));

  const PIPELINE_STAGES = new Set(['rate_confirmed', 'contracted', 'complete']);

  const talentStats = (id) => {
    const own = deals.filter((d) => dealTalentIds(d).includes(id));
    // Pipeline = 100% of deal value for any deal in rate_confirmed / contracted / complete (all time)
    const pipeline = own.filter((d) => PIPELINE_STAGES.has(d.stage)).reduce((s, d) => s + (Number(d.value) || 0), 0);
    const paid = own.filter((d) => d.paid_out_at).reduce((s, d) => s + (Number(d.value) || 0), 0);
    return { count: own.length, pipeline, paid };
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">talent</h1>
          <p className="text-sm text-slate-500 mt-1">your roster. add someone by their Gmail and they can sign in.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> add talent
        </Button>
      </header>

      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search talent…" className="pl-9 h-10 bg-white" />
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-12 text-center">loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <p className="text-slate-500 text-sm">no talent yet. add your first creator to get started.</p>
          <Button onClick={() => setOpen(true)} className="mt-4 bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> add talent
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((t) => {
            const s = talentStats(t.user_id);
            return (
              <div key={t.user_id} onClick={() => navigate(`/talent/${t.user_id}`)} className="group cursor-pointer bg-white border border-slate-200 hover:border-mint-300 hover:shadow-md rounded-2xl p-5 transition">
                <div className="flex items-start gap-3">
                  <InitialsAvatar name={t.name} size={56} className="ring-2 ring-white shadow-sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">{t.name}</h3>
                      {!t.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">inactive</span>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{t.handle || t.email}</p>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${t.name} from the roster? Their deals will be deleted.`)) removeTalent(t.user_id); }} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-4 flex items-center gap-3 text-slate-400">
                  {t.instagram && <Instagram className="w-4 h-4" />}
                  {t.tiktok && <Music2 className="w-4 h-4" />}
                  {t.youtube && <Youtube className="w-4 h-4" />}
                  <Mail className="w-4 h-4 ml-auto" />
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">deals</div><div className="text-sm font-semibold text-slate-800">{s.count}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">pipeline</div><div className="text-sm font-semibold text-slate-800">{fmtMoney(s.pipeline)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wide text-slate-400">paid</div><div className="text-sm font-semibold text-mint-700">{fmtMoney(s.paid)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddTalentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
};

export default Talent;
